import { Section } from '../../../views/sections/builders/section_builder';
const _4 = require('lodash4');
const moment = require('moment');
import {
  ISection,
  ITimelineFooter,
  ISectionTime,
  ITimeBlocks,
  ITimelineTimeblock,
  ITerm
} from '../../../interfaces/scheduler_interfaces';

interface ITimeblockParams {
  section: Section;
  dateRangeForBody: string;
  isRegistered: boolean;
  timeblockColor: object;
  isShortSection: boolean;
  isOnline: boolean;
  blocks: ITimelineTimeblock[];
  timeblockResults: ITimeBlocks[];
}
export class TimelineServant {
  getTimelineBlock(params: ITimeblockParams): ITimeBlocks {
    const isPreview = params.section.status === 'PREVIEW';
    const hasDuplicateSectionTitle =
      _4.filter(params.timeblockResults, { title: _4.get(params.section, '_course.cd', '') }).length > 0;

    const timeblock: ITimeBlocks = {
      title: hasDuplicateSectionTitle ? '' : _4.get(params.section, '_course.cd', ''),
      headerId: `month-${moment(params.section.class_start_dt).month()}`,
      isAcceleratedSection: false,
      color: params.timeblockColor,
      isRegistered: params.isRegistered,
      isPreview,
      isShortSection: params.isShortSection,
      isOnline: params.isOnline,
      blocks: params.blocks,
      section: params.section as ISection
    };

    return timeblock;
  }

  trimTrailingComma(value: string) {
    return _4.trimEnd(value, ', ');
  }
  getHeaders(range: string[]) {
    return _4.map(range, (item, index) => {
      const isFirstIndex = index === 0;
      const isLastIndex = index === range.length - 1;
      return {
        title: moment(item).format('MMMM'),
        id: `month-${moment(item).month()}`,
        start: isFirstIndex ? moment(item).startOf('week').unix() : moment(item).startOf('month').unix(),
        end: isLastIndex ? moment(item).endOf('week').unix() : moment(item).endOf('month').unix()
      };
    });
  }

  getEarliestDate(dates: string[]) {
    const filteredDates = _4.filter(dates, (date) => !_4.isUndefined(date));
    return _4.first(
      _4.sortBy(
        _4.map(filteredDates, (str) => moment(str, 'YYYY-MM-DD')),
        (a) => a.unix()
      )
    );
  }

  getLatestDate(dates: string[]) {
    const filteredDates = _4.filter(dates, (date) => !_4.isUndefined(date));
    return _4.last(
      _4.sortBy(
        _4.map(filteredDates, (str) => moment(str, 'YYYY-MM-DD')),
        (a) => a.unix()
      )
    );
  }

  static getTimelineDateRange(termInfo: ITerm, _fromDate: string, _toDate: string) {
    return {
      fromDate: _4.max([moment(_4.get(termInfo, 'term_start_dt')), moment(_fromDate)]).format('YYYY-MM-DD'),
      toDate: _4.min([moment(_4.get(termInfo, 'term_end_dt')), moment(_toDate)]).format('YYYY-MM-DD')
    };
  }

  getMultipleMeetingTimes(section: ISection, termInfo: ITerm): ITimelineTimeblock[] {
    const blocks: ITimelineTimeblock[] = [];
    const sectionTimes = _4.get(section, '_section_times', []);
    const hasMultipleSectionTimes = sectionTimes.length > 0;
    const hasSingleSectionTime = !hasMultipleSectionTimes;

    if (!_4.get(section, '_section_times')) {
      return blocks;
    }
    _4.each(section._section_times, (sectionTime) => {
      const { fromDate, toDate } = TimelineServant.getTimelineDateRange(
        termInfo,
        _4.get(sectionTime, 'from_dt') || section.class_start_dt,
        _4.get(sectionTime, 'to_dt') || section.class_end_dt
      );

      const hasFromToDates = fromDate && toDate;

      if (hasFromToDates) {
        blocks.push(this.__getBlock(section, fromDate, toDate));
      }
    });

    if (hasSingleSectionTime) {
      const { fromDate, toDate } = TimelineServant.getTimelineDateRange(
        termInfo,
        section.class_start_dt,
        section.class_end_dt
      );
      blocks.push(this.__getBlock(section, fromDate, toDate));
    }

    return blocks;
  }

  __isFewDays(fromDate: string, toDate: string) {
    return moment(toDate).diff(moment(fromDate), 'days') < 3;
  }

  __getBlockTitle(section: ISection, dateFormat: string = 'MMM DD') {
    let prefix: string = '';
    let sectionName: string = '';
    const sectionType = _4.get(section, '_section_type');
    const hasSectionType = !_4.isEmpty(sectionType);
    let returnString = '';
    if (hasSectionType) {
      sectionName = _4.lowerCase(`${section._section_type.name}`);
      prefix = `${_4.startCase(sectionName)} - `;
    }
    returnString = `${prefix}${moment(section['class_start_dt']).format(dateFormat)} - ${moment(
      section['class_end_dt']
    ).format(dateFormat)}`;
    return returnString;
  }

  __getBlock(section: ISection, fromDate: string, toDate: string): ITimelineTimeblock {
    const justifyContent = this.__isFewDays(fromDate, toDate) ? 'center' : '';
    return {
      displayText: this.__isFewDays(fromDate, toDate) ? '' : this.__getBlockTitle(section),
      description: this.__getBlockTitle(section, 'MMM DD, YYYY'),
      start: moment(fromDate).startOf('day').unix(),
      end: moment(toDate).endOf('day').unix(),
      startDate: fromDate,
      endDate: toDate,
      justifyContent
    };
  }

  sectionTimesDifferentFromTerm(section: ISection, termStart: string, termEnd: string, footer: ITimelineFooter[]) {
    const timelineFooter: ITimelineFooter[] = footer;
    _4.each(section._section_times, (sectionTime: ISectionTime) => {
      const sectionFromDate = _4.get(sectionTime, 'from_dt', '');
      const sectionToDate = _4.get(sectionTime, 'to_dt', '');
      const isFewDays = this.__isFewDays(sectionFromDate, sectionToDate);

      if (sectionFromDate && sectionFromDate !== termStart) {
        timelineFooter.push({
          start: moment(_4.get(sectionTime, 'from_dt')).startOf('day').unix(),
          date: sectionTime.from_dt || '',
          title: ''
        });
      }
      if (!isFewDays && sectionToDate && sectionToDate !== termEnd) {
        timelineFooter.push({
          start: moment(_4.get(sectionTime, 'to_dt')).endOf('day').unix(),
          date: sectionTime.to_dt || '',
          title: ''
        });
      }
    });
    return timelineFooter;
  }

  sectionDifferentFromTermStartEnd(section: ISection, termStart: string, termEnd: string, footer: ITimelineFooter[]) {
    const timelineFooter: ITimelineFooter[] = footer;
    if (section.class_start_dt !== termStart) {
      timelineFooter.push({
        start: moment(section.class_start_dt).startOf('day').unix(),
        date: section.class_start_dt,
        title: ''
      });
    }
    if (section.class_end_dt !== termEnd) {
      timelineFooter.push({
        start: moment(section.class_end_dt).endOf('day').unix(),
        date: section.class_end_dt,
        title: ''
      });
    }
    return timelineFooter;
  }

  getFooters(sections: ISection[], termStart: string, termEnd: string) {
    let footer: ITimelineFooter[] = [];
    _4.each(sections, (section: ISection) => {
      const hasMultipleSectionTimes = _4.get(section, '_section_times');
      if (hasMultipleSectionTimes) {
        footer = this.sectionTimesDifferentFromTerm(section, termStart, termEnd, footer);
      } else {
        footer = this.sectionDifferentFromTermStartEnd(section, termStart, termEnd, footer);
      }
    });
    footer = _4.sortBy(footer, 'start');
    if (!_4.isUndefined(footer[0])) {
      footer[0].title = 'Schedule Change';
    }
    return footer;
  }

  prependBlockCount(blocks: ITimeBlocks[]) {
    blocks = _4.sortBy(blocks, 'start');
    _4.each(blocks, (block) => {
      _4.each(block.blocks, (timelineBlock: ITimelineTimeblock, index) => {
        const numberOfBlocks = block.blocks.length;
        const isFewDays = this.__isFewDays(timelineBlock.startDate, timelineBlock.endDate);
        if (isFewDays) {
          return;
        }
        timelineBlock.displayText =
          numberOfBlocks > 1 ? `${index + 1}: ${timelineBlock.displayText}` : timelineBlock.displayText;
      });
    });
    return blocks;
  }
  getUniqueDates(blocks: ITimeBlocks[]) {
    _4.each(blocks, (block: ITimeBlocks) => {
      block.blocks = _4.uniqWith(
        block.blocks,
        (blockA: ITimelineTimeblock, blockB: ITimelineTimeblock) =>
          blockA.start === blockB.start && blockA.end === blockB.end
      );
    });
    return blocks;
  }
  sortBySectionStartDate(blocks: ITimeBlocks[]) {
    _4.each(blocks, (block: ITimeBlocks) => {
      block.blocks = _4.sortBy(block.blocks, 'start');
    });
    return blocks;
  }
}



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-scheduler/src/components/timeline/servants/timeline_servant.ts